import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { SectionCard, CategoryBadge, fmtPT } from "./shared";
import { SuppressionDetailDialog } from "./SuppressionDetailDialog";

const PAGE_SIZE = 25;

export function SuppressionHistory() {
    const [search, setSearch] = useState("");
    const [applied, setApplied] = useState({ search: "", category: "all", source: "all" });
    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [openId, setOpenId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, page_size: PAGE_SIZE };
            if (applied.search) params.search = applied.search;
            if (applied.category !== "all") params.category = applied.category;
            if (applied.source !== "all") params.source = applied.source;
            const { data: d } = await api.get("/reports/sms-suppression/history", { params });
            setData(d);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [applied, page]);

    useEffect(() => { load(); }, [load]);

    const apply = (patch) => { setPage(1); setApplied((a) => ({ ...a, ...patch })); };
    const pages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

    return (
        <SectionCard testId="suppression-history" title="Suppression history"
                     sub="Search by full phone number or the last 4 digits. Numbers are masked; admins can reveal one in the detail view.">
            <form className="flex flex-wrap gap-2 mb-4" onSubmit={(e) => { e.preventDefault(); apply({ search: search.trim() }); }}>
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Phone or last 4 digits"
                       className="w-56" data-testid="history-search-input" />
                <Button type="submit" variant="outline" size="sm" data-testid="history-search-btn"><Search className="h-4 w-4 mr-1" />Search</Button>
                <Select value={applied.category} onValueChange={(v) => apply({ category: v })}>
                    <SelectTrigger className="w-44" data-testid="history-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All categories</SelectItem>
                        <SelectItem value="blocking">Blocking (DNC + Technical)</SelectItem>
                        <SelectItem value="marketing_dnc">Marketing DNC</SelectItem>
                        <SelectItem value="technical">Technical</SelectItem>
                        <SelectItem value="manual_review">Manual review</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={applied.source} onValueChange={(v) => apply({ source: v })}>
                    <SelectTrigger className="w-40" data-testid="history-source-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All sources</SelectItem>
                        <SelectItem value="live">Live capture</SelectItem>
                        <SelectItem value="backfill">Historical backfill</SelectItem>
                    </SelectContent>
                </Select>
                {loading ? <Loader2 className="h-4 w-4 animate-spin self-center" /> : null}
            </form>
            <HistoryTable rows={data?.rows || []} onOpen={setOpenId} />
            <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
                <span data-testid="history-total">{(data?.total ?? 0).toLocaleString()} records</span>
                <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)} data-testid="history-prev"><ChevronLeft className="h-4 w-4" /></Button>
                    <span data-testid="history-page">Page {page} of {Math.min(pages, 200)}</span>
                    <Button size="sm" variant="outline" disabled={page >= Math.min(pages, 200) || loading} onClick={() => setPage(page + 1)} data-testid="history-next"><ChevronRight className="h-4 w-4" /></Button>
                </div>
            </div>
            <SuppressionDetailDialog recordId={openId} onClose={() => setOpenId(null)} />
        </SectionCard>
    );
}

function HistoryTable({ rows, onOpen }) {
    if (!rows.length) return <div className="text-sm text-muted-foreground py-6 text-center" data-testid="history-empty">No matching records.</div>;
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="history-table">
                <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b">
                        <th className="py-2 pr-4">Phone</th><th className="py-2 pr-4">Category</th><th className="py-2 pr-4">Reasons</th>
                        <th className="py-2 pr-4">First seen</th><th className="py-2 pr-4">Last seen</th><th className="py-2 pr-4">Record created</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.id} onClick={() => onOpen(r.id)} className="border-b last:border-0 cursor-pointer hover:bg-gray-50 transition-colors"
                            data-testid={`history-row-${r.id}`}>
                            <td className="py-2 pr-4 font-mono">{r.phone_masked}</td>
                            <td className="py-2 pr-4"><CategoryBadge category={r.category} /></td>
                            <td className="py-2 pr-4 text-xs text-muted-foreground">{r.reasons.join(", ")}</td>
                            <td className="py-2 pr-4 text-xs">{fmtPT(r.first_seen_at)}</td>
                            <td className="py-2 pr-4 text-xs">{fmtPT(r.last_seen_at)}</td>
                            <td className="py-2 pr-4 text-xs">{fmtPT(r.record_created_at)} <span className="text-muted-foreground">({r.created_by})</span></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
